/**
 * Convex hull (Andrew's monotone chain) for the per-mode hull fills on the
 * cluster map (design doc 015 §6 — the "standard scatter-hull approach" the
 * sandbox `render_plotly` used via scipy's `ConvexHull`, ported to the browser
 * so it needs no native dependency).
 *
 * Pure and deterministic, so it is unit-tested directly.
 */

export interface Point {
  x: number;
  y: number;
}

/** 2-D cross product of OA × OB; >0 ⇒ counter-clockwise turn. */
function cross(o: Point, a: Point, b: Point): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/**
 * Return the convex hull of `points` as an ordered (counter-clockwise) ring of
 * vertices, without repeating the first vertex. Returns `[]` when the points do
 * not span a polygon (fewer than 3 distinct, non-collinear points) — the caller
 * simply draws no hull, exactly like the scipy `QhullError` fallback.
 */
export function convexHull(points: Point[]): Point[] {
  // Sort by x then y; dedupe coincident points (monotone chain assumes unique).
  const sorted = points
    .slice()
    .sort((a, b) => a.x - b.x || a.y - b.y);
  const uniq: Point[] = [];
  for (const p of sorted) {
    const last = uniq[uniq.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) uniq.push(p);
  }
  if (uniq.length < 3) return [];

  const lower: Point[] = [];
  for (const p of uniq) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper: Point[] = [];
  for (let i = uniq.length - 1; i >= 0; i--) {
    const p = uniq[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }

  // Drop each chain's last point (it is the first point of the other chain).
  lower.pop();
  upper.pop();
  const hull = lower.concat(upper);
  return hull.length >= 3 ? hull : [];
}

/** Vertices per buffered point — enough for hulls to read as smooth. */
const PAD_SEGMENTS = 16;

/**
 * Convex hull of `points` buffered outward by `radius`: each point is replaced
 * by a ring of `PAD_SEGMENTS` vertices at `radius` before hulling. Unlike
 * `convexHull`, for `radius > 0` this spans a polygon for ANY non-empty input —
 * a single point becomes a circle, two points a capsule, collinear points a
 * stadium — so every mode gets a visible boundary (#1274). With `radius <= 0`
 * it falls back to the plain `convexHull` (which may span nothing).
 */
export function paddedHull(points: Point[], radius: number): Point[] {
  if (points.length === 0) return [];
  if (radius <= 0) return convexHull(points);
  const buffered: Point[] = [];
  for (const p of points) {
    for (let i = 0; i < PAD_SEGMENTS; i++) {
      const a = (2 * Math.PI * i) / PAD_SEGMENTS;
      buffered.push({
        x: p.x + radius * Math.cos(a),
        y: p.y + radius * Math.sin(a),
      });
    }
  }
  return convexHull(buffered);
}
